import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Profile from "@/pages/Profile";
import Forum from "@/pages/Forum";
import CategoryPage from "@/pages/CategoryPage";
import NewTopic from "@/pages/NewTopic";
import TopicDetail from "@/pages/TopicDetail";
import Events from "@/pages/Events";
import NewEvent from "@/pages/NewEvent";
import EventDetail from "@/pages/EventDetail";
import AuthCallback from "@/pages/AuthCallback";
import About from "@/pages/About";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import ArticleEditor from "@/pages/ArticleEditor";
import Store from "@/pages/Store";
import ProductDetail from "@/pages/ProductDetail";
import ProductEditor from "@/pages/ProductEditor";
import Resources from "@/pages/Resources";
import AdminResourceEditor from "@/pages/AdminResourceEditor";
import AdminInquiries from "@/pages/AdminInquiries";
import AdminCategories from "@/pages/AdminCategories";
import AdminAbout from "@/pages/AdminAbout";
import AdminForumRules from "@/pages/AdminForumRules";
import AdminHero from "@/pages/AdminHero";
import AdminSiteSettings from "@/pages/AdminSiteSettings";
import AdminContentGeneric from "@/pages/AdminContentGeneric";
import AdminUsers from "@/pages/AdminUsers";

function AppShell() {
  // Render AuthCallback synchronously when session_id is in URL fragment
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Header />
      <main className="min-h-[calc(100vh-72px)]">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/hakkimizda" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          <Route path="/forum" element={<Forum />} />
          <Route path="/forum/:categorySlug" element={<CategoryPage />} />
          <Route path="/topic/:topicId" element={<TopicDetail />} />
          <Route
            path="/topic-new"
            element={
              <ProtectedRoute>
                <NewTopic />
              </ProtectedRoute>
            }
          />

          <Route path="/events" element={<Events />} />
          <Route path="/events/:eventId" element={<EventDetail />} />
          <Route
            path="/events-new"
            element={
              <ProtectedRoute adminOnly>
                <NewEvent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId/duzenle"
            element={
              <ProtectedRoute adminOnly>
                <NewEvent mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route path="/yazilar" element={<Articles />} />
          <Route path="/yazilar/:slug" element={<ArticleDetail />} />
          <Route
            path="/yazilar/yeni"
            element={
              <ProtectedRoute adminOnly>
                <ArticleEditor mode="new" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yazilar/:slug/duzenle"
            element={
              <ProtectedRoute adminOnly>
                <ArticleEditor mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route path="/magaza" element={<Store />} />
          <Route
            path="/magaza/yeni"
            element={
              <ProtectedRoute adminOnly>
                <ProductEditor mode="new" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/magaza/:productId/duzenle"
            element={
              <ProtectedRoute adminOnly>
                <ProductEditor mode="edit" />
              </ProtectedRoute>
            }
          />
          <Route path="/magaza/:slug" element={<ProductDetail />} />

          <Route path="/kaynaklar" element={<Resources />} />
          <Route
            path="/kaynaklar/yeni"
            element={
              <ProtectedRoute adminOnly>
                <AdminResourceEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/talepler"
            element={
              <ProtectedRoute adminOnly>
                <AdminInquiries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/kategoriler"
            element={
              <ProtectedRoute adminOnly>
                <AdminCategories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/hakkimizda"
            element={
              <ProtectedRoute adminOnly>
                <AdminAbout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/kurallar"
            element={
              <ProtectedRoute adminOnly>
                <AdminForumRules />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/anasayfa"
            element={
              <ProtectedRoute adminOnly>
                <AdminHero />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ayarlar"
            element={
              <ProtectedRoute adminOnly>
                <AdminSiteSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/icerik/:key"
            element={
              <ProtectedRoute adminOnly>
                <AdminContentGeneric />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/uyeler"
            element={
              <ProtectedRoute adminOnly>
                <AdminUsers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      <footer className="border-t border-cream-300 bg-cream-100/60">
        <div className="k-container py-8 text-center font-body text-sm text-ink-500">
          <p>
            <span className="font-display text-2xl text-ink-900">kabile</span> · Eğitmen ve Ebeveyn Topluluğu
          </p>
          <div className="mt-3 flex items-center justify-center gap-3" data-testid="footer-badges">
            <span className="font-body">Ücretsiz üyelik</span>
            <span className="h-1 w-1 rounded-full bg-ink-500/40" />
            <span className="font-body">KVKK'ya uygun</span>
          </div>
          <p className="mt-3">
            İletişim:{" "}
            <a
              href="mailto:kabilewaldorf@gmail.com"
              className="font-semibold text-sage-700 hover:underline"
              data-testid="footer-contact-email"
            >
              kabilewaldorf@gmail.com
            </a>
          </p>
        </div>
      </footer>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#FBF8F1",
                color: "#3E362E",
                border: "1px solid #DCD3C6",
                fontFamily: "Nunito, sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
